'use client';

import { useEffect, useState } from 'react';
import { getRelatedCaseStudies } from '@/app/utils/relatedContentHandler';
import { CollectionCard } from './collection-type/components/collectionCard';
import LoadingPosts from '@/components/shared/LoadingPosts';

// Fixed 4-column grid regardless of item count — cards keep their natural
// size and any unused columns stay empty, so 2 items don't stretch to fill
// the container.
const GRID_CLASSES = 'grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

// The auto-gen handler expects comma-separated key strings for
// `categories` and `tags`. GraphQL returns categories as an array of
// DotCategory objects and tags as either an array of strings or a
// comma-separated string, so normalize before handing off.
function toKeyString(value, key = 'key') {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (!Array.isArray(value)) return '';
    return value
        .map((v) => (typeof v === 'string' ? v : v?.[key]))
        .filter(Boolean)
        .join(',');
}

// The SDK's depth=1 fetch materializes CaseStudy.image into a nested
// `{ fileAsset: { idPath, ... } }` object. CollectionCard's image
// branch only recognizes a top-level `{ identifier }` shape or a bare
// string, so extract the usable path and hand it back as a string.
function normalizeCaseStudyForCard(contentlet) {
    const image = contentlet?.image;
    if (!image || typeof image === 'string') return contentlet;
    const idPath = image.fileAsset?.idPath || image.fileAsset?.versionPath || image.idPath;
    if (!idPath) return contentlet;
    return { ...contentlet, image: idPath };
}

function RelatedCaseStudies(props) {
    const { widgetTitle, maxLimit = 4 } = props;
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await getRelatedCaseStudies({
                    ...props,
                    maxLimit,
                    relatedCaseStudiesCategories: toKeyString(props.relatedCaseStudiesCategories),
                    relatedCaseStudiesTags: toKeyString(props.relatedCaseStudiesTags)
                });
                if (!cancelled) setStudies(Array.isArray(result) ? result : []);
            } catch (err) {
                console.error('Error fetching related case studies:', err);
                if (!cancelled) setStudies([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) return <LoadingPosts className="container mx-auto mt-12" />;
    if (!studies.length) return null;

    const visible = studies.slice(0, maxLimit);

    return (
        <section data-component="related-case-studies" className="container mx-auto">
            {widgetTitle && (
                <h2 className="mb-8 text-2xl font-bold leading-tight dark:text-white">
                    {widgetTitle}
                </h2>
            )}
            <ul className={GRID_CLASSES}>
                {visible.map((contentlet, i) => (
                    <li
                        key={contentlet.identifier || i}
                        className="card-hover-pop relative cursor-pointer rounded-lg shadow-md">
                        <CollectionCard
                            contentlet={normalizeCaseStudyForCard(contentlet)}
                            contenttype="CaseStudy"
                            showCategory={true}
                            headerType="h3"
                        />
                    </li>
                ))}
            </ul>
        </section>
    );
}

export default RelatedCaseStudies;
